import {
  Autocomplete,
  Backdrop,
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Iconify from 'src/components/Iconify';
import { WrappedPatient } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient";
import { CreatePatientEngagementDto } from "src/careflow/engagement-hub/patient-engagement/service/dto/create-patient-engagement.dto";
import useLocales from "src/careflow/ph-core/internationalization/hook/useLocales";
import { checkAclValidation } from "src/careflow/ph-core/admin/permissions/util/permission.utils";
import crsAcls from "src/careflow/ph-core/admin/permissions/util/crs/crsAcls";
import agent from "src/api/agent";
import CancelButton from "src/careflow/common/CancelButton";
import { PatientEngagementService } from "src/careflow/engagement-hub/patient-engagement/service/PatientService";
import { WorkflowService } from "src/careflow/ph-core/workflow/api/WorkflowService";

type Props = {
  patient: WrappedPatient | null;
  handlerRefresh: Function;
};

export default function CreatePatientEngagement({
  patient,
  handlerRefresh
}: Props) {

  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const [openModal, setOpenModal] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const addPermission = checkAclValidation({ acls: [crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.ADD] });
  const [workflows, setWorkflows] = useState<any>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handlerCreatePatientEngagement = async () => {
    if(!selectedWorkflow && !selectedWorkflow?.code) {
      setError(true);
      return;
    };
    setOpenBackdrop(true);
    try {
      const payload: CreatePatientEngagementDto = {
        fhirId: patient?.id ?? '',
        language: patient?.getLanguage()?.[0]?.value ?? '',
        scope: selectedWorkflow?.code ?? ''
      };
  
      await PatientEngagementService.createPatientEngagement(payload);
      enqueueSnackbar(i18n('patients.details.engagement.msgCreated', 'crs'));
      handlerRefresh();
    } catch (error) {
      console.log(error);
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    } finally{
      setOpenModal(false);
      setOpenBackdrop(false);
    }
  };

  const getWorkflowsByType = async () => {
    setLoading(true);
    const workflowsByType = await WorkflowService.getWorkflowByType('ehub-box');
    const onlyActive = workflowsByType.filter((item) => item.status === 'Active');
    if(onlyActive?.length === 1) setSelectedWorkflow(onlyActive?.[0]);
    setWorkflows(onlyActive);
    setLoading(false);
  };

  useEffect(() => {
    if (openModal) {
      setError(false);
      getWorkflowsByType();
    } else {
      setSelectedWorkflow(null);
    }
  }, [openModal]);

  return(
    <>
      {addPermission && (
        <Button
          variant="contained"
          disabled={false}
          sx={{ height: '36px' }}
          onClick={() => setOpenModal(true)}
          startIcon={<Iconify icon={'eva:plus-fill'} />}
          style={{ position: 'absolute', top: '25px', right: '25px' }}
        >
          {i18n('patients.details.engagement.button', 'crs')}
        </Button>
      )}

      <Dialog 
        open={openModal}
        fullWidth
        maxWidth="sm"
      >
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={openBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <DialogTitle>{i18n('patients.details.engagement.button', 'crs')}</DialogTitle>
        <DialogContent>
          <Stack direction="row" alignItems="center">
            <Typography mt={2.5}>{`${i18n('patients.details.engagement.message', 'crs')}`}</Typography>
            <Autocomplete
              value={selectedWorkflow}
              sx={{ width: '48%'}}
              onChange={(_: React.SyntheticEvent, worflow) => {
                setError(false);
                setSelectedWorkflow(worflow);
              }}
              options={workflows ?? []}
              getOptionLabel={({ name }) => name ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!error}
                  helperText={error && "Required"}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />?
          </Stack>
        </DialogContent>
        <DialogActions>
          <CancelButton 
            onClick={() => setOpenModal(false)}
          >
            {i18n('cancel')}
          </CancelButton>

          <Button 
            disabled={loading || workflows?.length === 0} 
            variant="contained" 
            onClick={handlerCreatePatientEngagement}
          >
            {i18n('patients.details.engagement.agree', 'crs')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}